
export class ArrayHelper {

    static remove<T>(array: T[], elem: T): T[] {
        const index: number = array.indexOf(elem);
        return array.splice(index, 1);
    }

    static tail<T>(array: T[], n: number = 0): T {
        return array[array.length - (1 + n)];
    }

    static removeIf<T>(array: T[], callback: (item: T) => boolean) {
        let i = array.length;
        while (i--) {
            if (callback(array[i])) {
                array.splice(i, 1);
            }
        }
    }

    static equals<T>(one: T[], other: T[], itemEquals: (a: T, b: T) => boolean = (a, b) => a === b): boolean {
        if (one.length !== other.length) {
            return false;
        }

        for (let i = 0, len = one.length; i < len; i++) {
            if (!itemEquals(one[i], other[i])) {
                return false;
            }
        }

        return true;
    }

    static findIndex<T = any>(array: T[], predicate: (el: T) => boolean) {
        // 1. Let O be ? ToObject(array value).
        if (array == null) {
            throw new TypeError('"array" is null or not defined');
        }

        const o = Object(array);

        // 2. Let len be ? ToLength(? Get(O, "length")).
        const len = o.length >>> 0;

        // 3. If IsCallable(predicate) is false, throw a TypeError exception.
        if (typeof predicate !== 'function') {
            throw new TypeError('predicate must be a function');
        }

        // 5. Let k be 0.
        let k = 0;

        // 6. Repeat, while k < len
        while (k < len) {
            // a. Let Pk be ! ToString(k).
            // b. Let kValue be ? Get(O, Pk).
            // c. Let testResult be ToBoolean(? Call(predicate, T, « kValue, k, O »)).
            // d. If testResult is true, return k.
            const kValue = o[k];
            if (predicate.call(array, kValue, k, o)) {
                return k;
            }
            // e. Increase k by 1.
            k++;
        }

        // 7. Return -1.
        return -1;
    }

    static binarySearch<T>(array: T[], key: T, comparator: (op1: T, op2: T) => number): number {
        let low = 0,
            high = array.length - 1;

        while (low <= high) {
            const mid = ((low + high) / 2) | 0;
            const comp = comparator(array[mid], key);
            if (comp < 0) {
                low = mid + 1;
            } else if (comp > 0) {
                high = mid - 1;
            } else {
                return mid;
            }
        }
        return -(low + 1);
    }

    /**
     * Takes a sorted array and a function p. The array is sorted in such a way that all elements where p(x) is false
     * are located before all elements where p(x) is true.
     * @returns the least x for which p(x) is true or array.length if no element fullfills the given function.
     */
    static findFirst<T>(array: T[], p: (x: T) => boolean): number {
        let low = 0, high = array.length;
        if (high === 0) {
            return 0; // no children
        }
        while (low < high) {
            const mid = Math.floor((low + high) / 2);
            if (p(array[mid])) {
                high = mid;
            } else {
                low = mid + 1;
            }
        }
        return low;
    }

    /**
     * Returns the top N elements from the array.
     *
     * Faster than sorting the entire array when the array is a lot larger than N.
     *
     * @param array The unsorted array.
     * @param compare A sort function for the elements.
     * @param n The number of elements to return.
     * @return The first n elemnts from array when sorted with compare.
     */
    static top<T>(array: T[], compare: (a: T, b: T) => number, n: number): T[] {
        if (n === 0) {
            return [];
        }
        const result = array.slice(0, n).sort(compare);
        for (let i = n, m = array.length; i < m; i++) {
            const element = array[i];
            if (compare(element, result[n - 1]) < 0) {
                result.pop();
                const j = ArrayHelper.findFirst(result, e => compare(element, e) < 0);
                result.splice(j, 0, element);
            }
        }
        return result;
    }

    /**
     * @returns a new array with all undefined or null values removed. The original array is not modified at all.
     */
    static coalesce<T>(array: T[]): T[] {
        if (!array) {
            return array;
        }

        return array.filter(e => !!e);
    }

    /**
     * Moves the element in the array for the provided positions.
     */
    static move(array: any[], from: number, to: number): void {
        array.splice(to, 0, array.splice(from, 1)[0]);
    }

    /**
     * @returns {{false}} if the provided object is an array
     * 	and not empty.
     */
    static isFalsyOrEmpty(obj: any): boolean {
        return !Array.isArray(obj) || (<Array<any>>obj).length === 0;
    }

    /**
     * Removes duplicates from the given array. The optional keyFn allows to specify
     * how elements are checked for equalness by returning a unique string for each.
     */
    static distinct<T>(array: T[], keyFn?: (t: T) => string): T[] {
        if (!keyFn) {
            return array.filter((element, position) => {
                return array.indexOf(element) === position;
            });
        }

        const seen: { [key: string]: boolean; } = Object.create(null);
        return array.filter((elem) => {
            const key = keyFn(elem);
            if (seen[key]) {
                return false;
            }

            seen[key] = true;

            return true;
        });
    }

    static uniqueFilter<T>(keyFn: (t: T) => string): (t: T) => boolean {
        const seen: { [key: string]: boolean; } = Object.create(null);

        return element => {
            const key = keyFn(element);

            if (seen[key]) {
                return false;
            }

            seen[key] = true;
            return true;
        };
    }

    static firstIndex<T>(array: T[], fn: (item: T) => boolean): number {
        for (let i = 0; i < array.length; i++) {
            const element = array[i];

            if (fn(element)) {
                return i;
            }
        }

        return -1;
    }

    static first<T>(array: T[], fn: (item: T) => boolean, notFoundValue: T = null): T {
        const index = ArrayHelper.firstIndex(array, fn);
        return index < 0 ? notFoundValue : array[index];
    }

    static commonPrefixLength<T>(one: T[], other: T[], equals: (a: T, b: T) => boolean = (a, b) => a === b): number {
        let result = 0;

        for (let i = 0, len = Math.min(one.length, other.length); i < len && equals(one[i], other[i]); i++) {
            result++;
        }

        return result;
    }

    static flatten<T>(arr: T[][]): T[] {
        return arr.reduce((r, v) => r.concat(v), []);
    }

    static range(to: number, from = 0): number[] {
        const result: number[] = [];

        for (let i = from; i < to; i++) {
            result.push(i);
        }

        return result;
    }

    static fill<T>(num: number, valueFn: () => T, arr: T[] = []): T[] {
        for (let i = 0; i < num; i++) {
            arr[i] = valueFn();
        }

        return arr;
    }

    static index<T>(array: T[], indexer: (t: T) => string): { [key: string]: T; };
    static index<T, R>(array: T[], indexer: (t: T) => string, merger?: (t: T, r: R) => R): { [key: string]: R; };
    static index<T, R>(array: T[], indexer: (t: T) => string, merger: (t: T, r: R) => R = t => t as any): { [key: string]: R; } {
        return array.reduce((r, t) => {
            const key = indexer(t);
            r[key] = merger(t, r[key]);
            return r;
        }, Object.create(null));
    }


}
Array.prototype["remove"] = function (value) {
    const idx = this.indexOf(value);
    if (idx != -1) {
        return this.splice(idx, 1); // The second parameter is the number of elements to remove.
    }
    return false;
}


Array.prototype["insert"] = function (value) {
    this.push(value);

    return () => {
        const index = this.indexOf(value);
        if (index > -1) {
            this.splice(index, 1);
        }
    };
}
